import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback } from 'react';

import { defaultNS, useTranslation } from '@/lib/i18n';

import { AlertPage } from '../-shared/AlertPage';
import { BulkProvisionButton } from '../-shared/BulkProvisionButton';
import { createSearchSchema } from '../-shared/search-schema';
import type { AlertRowWithWorker, AlertSearchParams } from '../-shared/types';
import {
  createAlertExportHook,
  createAlertQueryHook,
} from '../-shared/useAlertQuery';
import { SoftwareAlertType } from '../../-zod-schema';

import {
  ColumnKey,
  SortableColumnKey,
  useSoftwareAlertsNoUserColumns,
} from './-hooks/useColumns';

const SearchSchema = createSearchSchema(SortableColumnKey);

const useAlertQuery = createAlertQueryHook<AlertRowWithWorker>({
  category: 'NO_USER',
  queryKeySegment: 'no-user',
  sortColumnMap: {
    [ColumnKey.appUser]: 'USER',
    [ColumnKey.worker]: 'PERSON',
    [ColumnKey.app]: 'APP',
    worker_assignedRoleName: 'APP_ASSIGNED_ROLE',
  },
  filterAndMap: (items): AlertRowWithWorker[] =>
    items
      .filter((item) => item.worker)
      .map((item) => ({
        ...item,
        worker: item.worker as NonNullable<typeof item.worker>,
      })),
});

const useExport = createAlertExportHook('NO_USER');

export const Route = createFileRoute(
  '/_staff/software/_layout/alerts/_alerts/no-user/',
)({
  validateSearch: zodValidator(SearchSchema),
  component: SoftwareAlertsNoUser,
});

function SoftwareAlertsNoUser() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const counts = useLoaderData({ from: Route.parentRoute.id });
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.alert',
  });

  const { data, isPending } = useAlertQuery(search);
  const { mutate: onExport, isPending: exporting } = useExport(search);
  const columns = useSoftwareAlertsNoUserColumns();

  const onSearch = useCallback(
    (query: Partial<AlertSearchParams>) => {
      navigate({ search: (prev) => ({ ...prev, ...query }) as typeof prev });
    },
    [navigate],
  );

  return (
    <AlertPage
      alertTypeKey={SoftwareAlertType.noUser.key}
      helpTitle={t('help.noUser.title')}
      helpText={t('help.noUser.text')}
      counts={counts}
      search={search}
      onSearch={onSearch}
      data={data}
      isPending={isPending}
      exporting={exporting}
      onExport={onExport}
      columns={columns}
      extraFilterActions={<BulkProvisionButton selectParam="noUser" />}
    />
  );
}
